import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Camera, Coins, Trophy, Upload } from "lucide-react";
import { api, brl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ContestCard } from "../components/ContestCard";
import { PhotoCard } from "../components/PhotoCard";
import { Lightbox } from "../components/Lightbox";
import { SectionHeader } from "../components/SectionHeader";

const HERO_IMG = "https://images.unsplash.com/photo-1608245449230-4ac19066d2d0?auto=format&fit=crop&w=2000&q=80";

const Hero = ({ settings, user }) => (
  <section className="noise scanline relative overflow-hidden border-b border-white/10" data-testid="home-hero">
    <img src={HERO_IMG} alt="" className="absolute inset-0 h-full w-full object-cover opacity-60" />
    <div className="grid-bg absolute inset-0" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0F] via-[#0A0A0F]/85 to-transparent" />
    <div className="relative mx-auto max-w-7xl px-5 py-24 lg:px-8 lg:py-36">
      <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-2 font-mono-tech text-xs uppercase tracking-[0.25em] text-cyan-300">
        <span className="pulse-dot h-2 w-2 rounded-full bg-lime-400" /> Concursos ao vivo
      </motion.p>
      <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mt-5 max-w-3xl font-display text-6xl font-black uppercase leading-[0.9] tracking-tight sm:text-7xl lg:text-8xl">
        Congele o <span className="text-lime-400">momento</span> da <span className="text-cyan-400">vitória</span>
      </motion.h1>
      <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mt-6 max-w-xl text-lg text-slate-300">
        {settings?.tagline}. Cadastre-se, envie <b className="text-lime-300">1 foto grátis</b> e dispute os principais concursos de fotografia esportiva do Brasil.
      </motion.p>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-10 flex flex-wrap gap-4">
        <Link to={user ? "/painel" : "/cadastro"} data-testid="hero-cta-register" className="btn-neon flex items-center gap-2 rounded-full px-7 py-3.5 font-semibold">
          <Upload className="h-4 w-4" /> {user ? "Enviar minha foto" : "Enviar foto grátis"}
        </Link>
        <Link to="/concursos" data-testid="hero-cta-contests" className="flex items-center gap-2 rounded-full border border-cyan-400/50 px-7 py-3.5 font-semibold text-cyan-300 transition-colors duration-200 hover:bg-cyan-400/10">
          Ver concursos <ArrowRight className="h-4 w-4" />
        </Link>
      </motion.div>
    </div>
  </section>
);

const Marquee = ({ cats }) => (
  <div className="overflow-hidden border-b border-white/10 bg-[#0E0E15] py-4">
    <div className="marquee flex w-max gap-12 whitespace-nowrap">
      {[...cats, ...cats, ...cats, ...cats].map((c, i) => (
        <span key={i} className="font-display text-2xl font-bold uppercase text-slate-600">
          {c.name} <span className="text-cyan-400">/</span>
        </span>
      ))}
    </div>
  </div>
);

const Categories = ({ cats }) => (
  <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8">
    <SectionHeader kicker="// Modalidades" title="Escolha sua arena" testId="home-categories-header">
      <Link to="/galeria" className="font-mono-tech text-xs uppercase tracking-widest text-cyan-300 hover:text-white">Galeria completa →</Link>
    </SectionHeader>
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4 md:gap-5">
      {cats.map((c, i) => (
        <motion.div key={c.slug} initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} className={i === 0 || i === 5 ? "md:col-span-2" : ""}>
          <Link to={`/galeria?cat=${c.slug}`} data-testid={`category-tile-${c.slug}`} className="group relative block h-52 overflow-hidden rounded-xl border border-white/10 transition-[border-color,box-shadow] duration-300 neon-border md:h-64">
            <img src={c.cover} alt={c.name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 to-black/10" />
            <div className="absolute bottom-0 p-5">
              <p className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-cyan-300">{c.tag}</p>
              <h3 className="font-display text-3xl font-black uppercase">{c.name}</h3>
              <p className="font-mono-tech text-xs text-slate-400">{c.photos_count} fotos</p>
            </div>
          </Link>
        </motion.div>
      ))}
    </div>
  </section>
);

const steps = [
  { icon: Camera, title: "Cadastre-se", text: "Crie sua conta e envie sua primeira foto totalmente grátis." },
  { icon: Coins, title: "Fotos extras", text: "Compre fotos avulsas ou pacotes com desconto via Stripe ou Mercado Pago." },
  { icon: Trophy, title: "Concursos principais", text: "Use suas fotos extras para disputar os grandes prêmios e o voto popular." },
];

const HowItWorks = ({ settings }) => (
  <section className="border-y border-white/10 bg-[#0E0E15]">
    <div className="mx-auto grid max-w-7xl gap-12 px-5 py-24 lg:grid-cols-12 lg:px-8">
      <div className="lg:col-span-5">
        <SectionHeader kicker="// Como funciona" title="Do clique ao pódio" />
        <div className="space-y-6">
          {steps.map((s, i) => (
            <div key={s.title} className="flex gap-5">
              <span className="font-display text-5xl font-black text-white/10">0{i + 1}</span>
              <div>
                <h4 className="flex items-center gap-2 font-display text-2xl font-bold uppercase"><s.icon className="h-5 w-5 text-cyan-400" /> {s.title}</h4>
                <p className="text-slate-400">{s.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:col-span-7" data-testid="home-pricing">
        <div className="glass cut-corner rounded-xl p-7">
          <p className="font-mono-tech text-xs uppercase tracking-widest text-lime-300">No cadastro</p>
          <p className="mt-3 font-display text-5xl font-black">GRÁTIS</p>
          <p className="mt-2 text-sm text-slate-400">{settings?.free_photos_on_signup} foto para a galeria e concursos abertos</p>
        </div>
        <div className="glass cut-corner rounded-xl p-7">
          <p className="font-mono-tech text-xs uppercase tracking-widest text-cyan-300">Foto extra</p>
          <p className="mt-3 font-display text-5xl font-black" data-testid="home-extra-price">{brl(settings?.extra_photo_price)}</p>
          <p className="mt-2 text-sm text-slate-400">por foto, válida para concursos principais</p>
        </div>
        {settings?.packages?.map((p) => (
          <div key={p.id} className="glass flex items-center justify-between rounded-xl p-6 sm:col-span-2" data-testid={`home-package-${p.id}`}>
            <div>
              <p className="font-display text-2xl font-bold uppercase">{p.name}</p>
              <p className="text-sm text-slate-400">{p.photos} fotos</p>
            </div>
            <p className="font-display text-3xl font-black text-cyan-300">{brl(p.price)}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default function Home() {
  const { settings, user } = useAuth();
  const [cats, setCats] = useState([]);
  const [contests, setContests] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/contests").then((r) => setContests(r.data.filter((c) => c.status === "active")));
    api.get("/photos", { params: { sort: "top", limit: 12 } }).then((r) => setPhotos(r.data));
  }, []);

  const main = contests.filter((c) => c.is_main);
  return (
    <div data-testid="home-page">
      <Hero settings={settings} user={user} />
      <Marquee cats={cats} />
      <section className="mx-auto max-w-7xl px-5 pt-24 lg:px-8">
        <SectionHeader kicker="// Em destaque" title="Concursos principais" testId="home-main-contests">
          <Link to="/concursos" className="font-mono-tech text-xs uppercase tracking-widest text-cyan-300 hover:text-white">Todos os concursos →</Link>
        </SectionHeader>
        <div className="grid gap-5 lg:grid-cols-3">
          {main.map((c, i) => <ContestCard key={c.id} contest={c} index={i} large />)}
        </div>
      </section>
      <Categories cats={cats} />
      <HowItWorks settings={settings} />
      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8">
        <SectionHeader kicker="// Hall da fama" title="Mais votadas" testId="home-hall-of-fame" />
        <div className="columns-1 gap-5 sm:columns-2 lg:columns-3">
          {photos.map((p, i) => <PhotoCard key={p.id} photo={p} rank={i + 1} index={i} onOpen={setOpen} />)}
        </div>
      </section>
      <Lightbox photo={open} onClose={() => setOpen(null)} />
    </div>
  );
}
